import { useMemo, useState, useEffect } from "react";
import { Form, Input, Select, Button, InputNumber, Checkbox, Row, Col, Space, Spin, TimePicker, notification } from "antd";
import { HomeOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import "./styles.scss";
import { useGetBranchesQuery } from "../../services/branches";
import PageBreadcrumb from "../../components/breadcrumb";
import { AllPackagesRoute, Home } from "../../routes/routepath";
import { useNavigate, useParams } from "react-router-dom";
import ImagePicker from "../../components/form/ImagePicker";
import { useGetPlanDetailQuery, useUpdatePlanMutation } from "../../services/package";
import { useGetGymKitInventoryQuery } from "../../services/inventory";
import { useGetBranchResourcesQuery } from "../../services/biometric";
import dayjs from "dayjs";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface InventorySelection {
  selected:  boolean;
  quantity:  number;
}

interface InventoryOption {
  label:    string;
  value:    string;
  disabled: boolean;
}

interface BiometricOption {
  label:    string;
  value:    string;
  disabled: boolean;
}

interface InventoryPickerProps {
  value?:    Record<string, InventorySelection>;
  onChange?: (val: Record<string, InventorySelection>) => void;
}

/* ─── Constants ──────────────────────────────────────────────────────── */

const PACKAGE_TYPE_OPTIONS = [
  { label: 'Membership',          value: 'membership'          },
  { label: 'Trial',               value: 'trial'               },
  { label: 'Membership Transfer', value: 'membership_transfer' },
  { label: 'Add On Package',      value: 'add_on_package'      },
];

const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No',  value: 'no'  },
];

const ACCESS_TYPE_OPTIONS = [
  { label: 'Peak Hours',     value: 'peak_hours'     },
  { label: 'Non-Peak Hours', value: 'non_peak_hours' },
];

const GENDER_OPTIONS = [
  { label: 'Male',   value: 'male'   },
  { label: 'Female', value: 'female' },
  { label: 'Any',    value: 'any'    },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const EditPackage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form]   = Form.useForm();

  const { data: branchesData }                                                = useGetBranchesQuery(undefined);
  const { data: packageData, isLoading: isLoadingPackage, error: packageError } =
    useGetPlanDetailQuery(id as any);
  const [updatePackage, { isLoading: updating }] = useUpdatePlanMutation();

  const [gymKitSelected, setGymKitSelected] = useState('no');

  const selectedBranches: string[] | undefined = Form.useWatch('branch', form);

  const { data: inventoryData } = useGetGymKitInventoryQuery(
    (selectedBranches?.length ? selectedBranches : undefined) as any,
    { skip: gymKitSelected !== 'yes' || !selectedBranches?.length }
  );

  const { data: biometricData } = useGetBranchResourcesQuery(
    (selectedBranches?.length ? selectedBranches : undefined) as any,
    { skip: !selectedBranches?.length }
  );

  useEffect(() => {
    if (!selectedBranches?.length) {
      form.setFieldsValue({ selectedInventory: undefined, biometricMachine: undefined });
    }
  }, [selectedBranches, form]);

  /* ── useMemo ── */

  const branchOptions = useMemo(() => {
    const list: any[] = (branchesData as any)?.data;
    if (!Array.isArray(list)) return [];
    return list.map((b: any) => ({
      label: `${b.name}${b.branchArea ? ` — ${b.branchArea}` : ''}`,
      value: b._id,
    }));
  }, [branchesData]);

  const inventoryOptions = useMemo<InventoryOption[]>(() => {
    if (!selectedBranches?.length) return [];
    const apiData = inventoryData as any;
    if (!apiData?.success || !Array.isArray(apiData.data)) return [];
    const options: InventoryOption[] = [];
    apiData.data.forEach((branchEntry: any) => {
      if (Array.isArray(branchEntry.inventory)) {
        branchEntry.inventory.forEach((item: any) => {
          options.push({
            label:    `${item.productName} - ${branchEntry.branchInfo.name} (Qty: ${item.quantity}) - ${item.warehouseName}`,
            value:    item._id,
            disabled: item.quantity === 0,
          });
        });
      }
    });
    return options;
  }, [inventoryData, selectedBranches]);

  const biometricMachineOptions = useMemo<BiometricOption[]>(() => {
    if (!selectedBranches?.length) return [];
    const apiData = biometricData as any;
    if (!apiData?.success || !Array.isArray(apiData.data?.machines)) return [];
    const options: BiometricOption[] = [];
    apiData.data.machines.forEach((branchEntry: any) => {
      if (Array.isArray(branchEntry.machines)) {
        branchEntry.machines.forEach((machine: any) => {
          options.push({
            label:    `${machine.machineId} - ${branchEntry.branchInfo.name} (${machine.floor || 'N/A'})`,
            value:    machine._id,
            disabled: machine.deleted_at !== null,
          });
        });
      }
    });
    return options;
  }, [biometricData, selectedBranches]);

  /* ── Populate form ── */

  useEffect(() => {
    const pkg: any = (packageData as any)?.data;
    if (!pkg) return;

    setGymKitSelected(pkg.gymKitAvailable ? 'yes' : 'no');

    const inventorySelection: Record<string, InventorySelection> = {};
    if (Array.isArray(pkg.products)) {
      pkg.products.forEach((product: any) => {
        const productId = product.productId?._id || product.productId;
        inventorySelection[productId] = { selected: true, quantity: product.quantity || 1 };
      });
    }

    const branchIds: string[] = pkg.branchIds?.map((b: any) => b._id || b) || [];

    const SERVICE_LABEL: Record<string, string> = {
      personal_training: 'Personal Training',
      pilates:           'Pilates',
      therapy:           'Therapy',
      ems:               'EMS',
      paid_locker:       'Paid Locker',
      mma:               'MMA',
    };

    form.setFieldsValue({
      packageType:            pkg.type,
      packageName:            pkg.name,
      price:                  pkg.pricing,
      validDays:              pkg.numberOfDays,
      hsnCode:                pkg.hsn_sac,
      freezable:              pkg.freezable ? 'yes' : 'no',
      freezeDays:             pkg.freezableDays    || 0,
      freezeSlot:             pkg.freezableSlot    || 0,
      branch:                 branchIds,
      benefitPoints:          pkg.benefits?.map((b: any) => ({ point: b.type })) || [],
      banner:                 pkg.photos?.[0]      || '',
      preferredGender:        pkg.preferredGender  || 'any',
      upgradeLimit:           pkg.upgradeLimit,
      advanceRenewDays:       pkg.advanceRenewDays || pkg.renewLimit || 0,
      assessment:             pkg.hasAssessments ? 'yes' : 'no',
      numberOfAssessments:    pkg.numberOfAssessment || 0,
      gymKit:                 pkg.gymKitAvailable ? 'yes' : 'no',
      selectedInventory:      inventorySelection,
      selectedBiometric:      pkg.machineIds?.map((m: any) => m._id || m) || [],
      accessType:             pkg.accessType       || 'peak_hours',
      nonPeakHours:           pkg.nonPeakHours?.map((slot: any) => ({
        from: slot.from ? dayjs(slot.from) : null,
        to:   slot.to   ? dayjs(slot.to)   : null,
      })) || [{ from: null, to: null }],
      complementaryServices:  pkg.complementaryServices?.map((svc: any) => ({
        serviceType: SERVICE_LABEL[svc.serviceType] ?? svc.serviceType,
        sessions:    svc.numberOfSessions,
      })) || [],
      maxMembers:             pkg.maxMembers,
      addonPackageName:       pkg.addonPackageName,
      addonPackagePrice:      pkg.addonPackagePrice,
      numberOfSession:        pkg.numberOfSessions,
      couponApplicable:       pkg.numberOfCoupons ? 'yes' : 'no',
      numberOfCoupons:        pkg.numberOfCoupons || undefined,
    });
  }, [packageData, form]);

  /* ── Inline component ── */

  const InventorySelectionWithQuantity = ({ value, onChange }: InventoryPickerProps) => {
    const [localSelection, setLocalSelection] = useState<Record<string, InventorySelection>>(value || {});

    useEffect(() => { if (value) setLocalSelection(value); }, [value]);

    const handleItemCheck = (itemId: string, checked: boolean) => {
      const next = { ...localSelection };
      if (checked) next[itemId] = { selected: true, quantity: 1 };
      else         delete next[itemId];
      setLocalSelection(next);
      onChange?.(next);
    };

    const handleQuantityChange = (itemId: string, qty: number | null) => {
      if (!localSelection[itemId]) return;
      const next = { ...localSelection, [itemId]: { ...localSelection[itemId], quantity: Math.max(qty || 1, 1) } };
      setLocalSelection(next);
      onChange?.(next);
    };

    return (
      <div style={{ maxHeight: 300, overflowY: 'auto', padding: 12, border: '1px solid #d9d9d9', borderRadius: 6 }}>
        {inventoryOptions.map(option => {
          const isSelected = localSelection[option.value]?.selected || false;
          const quantity   = localSelection[option.value]?.quantity  || 1;
          return (
            <Row key={option.value} align="middle" style={{ marginBottom: 12 }}>
              <Col span={16}>
                <Checkbox
                  checked={isSelected}
                  disabled={option.disabled}
                  onChange={e => handleItemCheck(option.value, e.target.checked)}
                >
                  {option.label}
                </Checkbox>
              </Col>
              {isSelected && (
                <Col span={8}>
                  <Space>
                    <span style={{ fontSize: 14, color: '#666' }}>Qty:</span>
                    <InputNumber
                      min={1}
                      value={quantity}
                      onChange={val => handleQuantityChange(option.value, val)}
                      size="small"
                      style={{ width: 80, height: 30 }}
                    />
                  </Space>
                </Col>
              )}
            </Row>
          );
        })}
        {inventoryOptions.length === 0 && (
          <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
            No inventory items available for selected branch
          </div>
        )}
      </div>
    );
  };

  /* ── Form handlers ── */

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message:     'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement:   'topRight',
      duration:    4,
    });
  };

  const handleUpdatePackage = async (values: Record<string, any>) => {
    const payload: Record<string, any> = {
      name:              values.packageName,
      pricing:           values.price,
      numberOfDays:      values.validDays,
      type:              values.packageType,
      hsnSac:            values.hsnCode,
      freezable:         values.freezable === 'yes',
      ...(values.freezable === 'yes' && {
        freezableDays: values.freezeDays,
        freezableSlot: values.freezeSlot,
      }),
      accessType:        values.accessType || 'peak_hours',
      ...(values.accessType === 'non_peak_hours' && values.nonPeakHours && {
        nonPeakHours: values.nonPeakHours.map((slot: any) => ({
          from: slot.from ? slot.from.toISOString() : null,
          to:   slot.to   ? slot.to.toISOString()   : null,
        })),
      }),
      branchIds:         Array.isArray(values.branch) ? values.branch : [values.branch],
      benefits:          values.benefitPoints ? values.benefitPoints.map((bp: any) => ({ type: bp.point })) : [],
      photos:            values.banner ? [values.banner] : [],
      description:       values.description   || '',
      preferredGender:   values.preferredGender || 'any',
      maxMembers:        values.maxMembers     || 0,
      upgradeLimit:      values.upgradeLimit   || 0,
      advanceRenewDays:  values.advanceRenewDays || 0,
      numberOfAssessment: values.assessment === 'yes' ? values.numberOfAssessments : 0,
      hasAssessments:    values.assessment === 'yes',
      gymKitAvailable:   values.gymKit === 'yes',
      products: values.gymKit === 'yes' && values.selectedInventory
        ? Object.entries(values.selectedInventory as Record<string, InventorySelection>).map(([productId, data]) => ({
            productId,
            quantity: data.quantity,
          }))
        : [],
      biometricMachines: values.selectedBiometric || [],
      machineIds:        values.selectedBiometric || [],
      ...(values.couponApplicable === 'yes' && { numberOfCoupons: values.numberOfCoupons }),
    };

    if (values.packageType === 'add_on_package') {
      payload.addonPackageName  = values.addonPackageName;
      payload.addonPackagePrice = values.addonPackagePrice;
      payload.numberOfSession   = values.numberOfSession;
    }

    Object.keys(payload).forEach(k => {
      if (payload[k] === undefined || payload[k] === null) delete payload[k];
    });

    try {
      await (updatePackage as any)({ id, body: payload }).unwrap();
      navigate(AllPackagesRoute);
    } catch (error) {
      console.error('Failed to update package', error);
    }
  };

  /* ── Early returns ── */

  if (isLoadingPackage) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>Loading package data...</p>
      </div>
    );
  }

  if (packageError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p style={{ color: 'red' }}>Error loading package data. Please try again.</p>
      </div>
    );
  }

  /* ── Render ── */

  const breadcrumbItems = [
    { label: <HomeOutlined />, to: Home },
    { label: 'All Packages',   to: AllPackagesRoute },
    { label: 'Edit Package' },
  ];

  return (
    <div className="add-package-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Edit Package</h2>
        <PageBreadcrumb items={breadcrumbItems} />
      </div>

      <div className="form-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdatePackage}
          onFinishFailed={onFinishFailed as any}
          className="trainer-form"
        >
          <div className="form-row">
            <Form.Item label="Package Type" name="packageType" rules={[{ required: true, message: 'Please select package type' }]}>
              <Select placeholder="Select package type" options={PACKAGE_TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item label="Package Name" name="packageName" rules={[{ required: true, message: 'Please enter package name' }]}>
              <Input placeholder="Enter package name" />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item label="Price" name="price" rules={[{ required: true, message: 'Please enter price' }]}>
              <InputNumber placeholder="Enter price" min={0} style={{ width: '100%' }} prefix="₹" />
            </Form.Item>
            <Form.Item label="Assessment" name="assessment" rules={[{ required: true, message: 'Please select assessment' }]}>
              <Select placeholder="Select assessment" options={YES_NO_OPTIONS} />
            </Form.Item>
          </div>

          <Form.Item noStyle shouldUpdate={(p, c) => p.assessment !== c.assessment}>
            {({ getFieldValue }) =>
              getFieldValue('assessment') === 'yes' ? (
                <div className="form-row-single">
                  <Form.Item label="Number of Assessments" name="numberOfAssessments" rules={[{ required: true, message: 'Please enter number of assessments' }]}>
                    <InputNumber placeholder="Enter number of assessments" min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </div>
              ) : null
            }
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(p, c) => p.packageType !== c.packageType}>
            {({ getFieldValue }) =>
              getFieldValue('packageType') === 'add_on_package' ? (
                <div className="form-row">
                  <Form.Item label="Max Members" name="maxMembers" rules={[{ required: true, message: 'Please enter max members' }]}>
                    <InputNumber placeholder="Enter max members" min={1} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Preferred Gender" name="preferredGender" rules={[{ required: true, message: 'Please select preferred gender' }]}>
                    <Select placeholder="Select preferred gender" options={GENDER_OPTIONS} />
                  </Form.Item>
                </div>
              ) : null
            }
          </Form.Item>

          <div className="form-row">
            <Form.Item label="Valid Days" name="validDays" rules={[{ required: true, message: 'Please enter valid days' }]}>
              <InputNumber placeholder="Enter valid days" min={1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="HSN/SAC Code" name="hsnCode" rules={[{ required: true, message: 'Please enter HSN/SAC code' }]}>
              <Input placeholder="Enter HSN/SAC code" />
            </Form.Item>
          </div>

          <div className="form-row-single">
            <Form.Item label="Branch" name="branch" rules={[{ required: true, message: 'Please select at least one branch' }]}>
              <Select mode="multiple" placeholder="Select branches" options={branchOptions} />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item label="Freezable" name="freezable">
              <Select placeholder="Select freezable option" options={YES_NO_OPTIONS} />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(p, c) => p.freezable !== c.freezable}>
              {({ getFieldValue }) =>
                getFieldValue('freezable') === 'yes' ? (
                  <>
                    <Form.Item label="Freeze Days" name="freezeDays">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Freeze Slot" name="freezeSlot">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>
          </div>

          <div className="form-row-single">
            <Form.Item label="Access Type" name="accessType" rules={[{ required: true, message: 'Please select access type' }]}>
              <Select placeholder="Select access type" options={ACCESS_TYPE_OPTIONS} />
            </Form.Item>
          </div>

          <Form.Item noStyle shouldUpdate={(p, c) => p.accessType !== c.accessType}>
            {({ getFieldValue }) =>
              getFieldValue('accessType') === 'non_peak_hours' ? (
                <>
                  <div style={{ marginBottom: 16 }}><h4 style={{ marginBottom: 12 }}>Non-Peak Hours</h4></div>
                  <Form.List name="nonPeakHours">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <div key={key} className="form-row" style={{ marginBottom: 12 }}>
                            <Form.Item {...rest} label="From Time" name={[name, 'from']} rules={[{ required: true, message: 'Please select start time' }]}>
                              <TimePicker format="HH:mm" placeholder="10:00" style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item {...rest} label="To Time" name={[name, 'to']} rules={[{ required: true, message: 'Please select end time' }]}>
                              <TimePicker format="HH:mm" placeholder="17:00" style={{ width: '100%' }} />
                            </Form.Item>
                            {fields.length > 1 && (
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ fontSize: 20, color: 'red', marginTop: 30, cursor: 'pointer' }} />
                            )}
                          </div>
                        ))}
                        <Form.Item>
                          <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} style={{ width: '100%' }}>
                            Add Time Slot
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </>
              ) : null
            }
          </Form.Item>

          <div className="form-row">
            <Form.Item label="Upgrade Limit" name="upgradeLimit">
              <InputNumber placeholder="Enter upgrade limit" min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Advance Renew Days" name="advanceRenewDays">
              <InputNumber placeholder="Enter advance renew days" min={0} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <div className="form-row-single">
            <Form.Item label="Gym Kit" name="gymKit">
              <Select
                placeholder="Select gym kit option"
                options={YES_NO_OPTIONS}
                onChange={(v: string) => {
                  setGymKitSelected(v);
                  if (v === 'no') form.setFieldsValue({ selectedInventory: {} });
                }}
              />
            </Form.Item>
          </div>

          {gymKitSelected === 'yes' && (
            <div className="form-row-single">
              <Form.Item label="Select Inventory Items with Quantities" name="selectedInventory">
                <InventorySelectionWithQuantity />
              </Form.Item>
            </div>
          )}

          <Form.Item noStyle shouldUpdate={(p, c) => p.packageType !== c.packageType || p.couponApplicable !== c.couponApplicable}>
            {({ getFieldValue }) =>
              getFieldValue('packageType') === 'membership' ? (
                <>
                  <div className="form-row-single">
                    <Form.Item label="Coupon Applicable" name="couponApplicable" rules={[{ required: true, message: 'Please select coupon option' }]}>
                      <Select
                        placeholder="Select Coupon option"
                        options={YES_NO_OPTIONS}
                        onChange={(v: string) => { if (v === 'no') form.setFieldsValue({ numberOfCoupons: undefined }); }}
                      />
                    </Form.Item>
                  </div>
                  {getFieldValue('couponApplicable') === 'yes' && (
                    <div className="form-row-single">
                      <Form.Item label="Number of Coupons" name="numberOfCoupons" rules={[{ required: true, message: 'Please enter number of coupons' }]}>
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="Enter number of coupons" />
                      </Form.Item>
                    </div>
                  )}
                </>
              ) : null
            }
          </Form.Item>

          <div className="form-row-single">
            <Form.Item label="Select Biometric Machines" name="selectedBiometric">
              <Checkbox.Group>
                <Row>
                  {biometricMachineOptions.map(option => (
                    <Col span={24} key={option.value} style={{ marginBottom: 8 }}>
                      <Checkbox value={option.value} disabled={option.disabled}>{option.label}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </div>

          <div className="form-row-single">
            <Form.Item label="Banner" name="banner">
              <ImagePicker form={form} name="banner" />
            </Form.Item>
          </div>

          <div className="certificates-section">
            <h3 style={{ color: 'var(--sider-text)', marginBottom: 16 }}>Benefit Points</h3>
            <Form.List name="benefitPoints">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <div key={key} className="certificate-item">
                      <div className="row">
                        <Form.Item {...rest} name={[name, 'point']} label="Benefit Point">
                          <Input placeholder="Enter benefit point" />
                        </Form.Item>
                        <Button type="link" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} style={{ marginTop: 30 }}>
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Benefit Point</Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>

          <Form.Item noStyle shouldUpdate={(p, c) => p.packageType !== c.packageType}>
            {({ getFieldValue }) =>
              getFieldValue('packageType') === 'add_on_package' ? (
                <>
                  <div className="form-separator" style={{ margin: '24px 0', borderBottom: '2px solid var(--border-color)' }} />
                  <h3 style={{ color: 'var(--sider-text)', marginBottom: 16 }}>Add-On Package Details</h3>
                  <div className="form-row">
                    <Form.Item label="Add on package name" name="addonPackageName" rules={[{ required: true, message: 'Please enter add-on package name' }]}>
                      <Input placeholder="Enter add-on package name" />
                    </Form.Item>
                    <Form.Item label="Add on package price" name="addonPackagePrice" rules={[{ required: true, message: 'Please enter add-on package price' }]}>
                      <InputNumber placeholder="Enter add-on package price" min={0} style={{ width: '100%' }} prefix="₹" />
                    </Form.Item>
                  </div>
                  <div className="form-row-single">
                    <Form.Item label="Number of session" name="numberOfSession" rules={[{ required: true, message: 'Please enter number of sessions' }]}>
                      <InputNumber placeholder="Enter number of sessions" min={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </div>
                </>
              ) : null
            }
          </Form.Item>

          <div className="form-actions">
            <Button onClick={() => navigate(AllPackagesRoute)} className="cancel-btn" size="large">Cancel</Button>
            <Button type="primary" htmlType="submit" loading={updating} className="submit-btn" size="large">
              Update Package
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default EditPackage;
